import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';

const SpectrumGraph = ({ data, soundmeterId, colorScheme = 'default' }) => {
  if (!data || !data.data || data.data.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-slate-500">
          Aucune donnée disponible pour l'analyse spectrale
        </CardContent>
      </Card>
    );
  }

  const colorSchemes = {
    default: ['#0d9488', '#14b8a6', '#2dd4bf', '#5eead4', '#99f6e4', '#ccfbf1'],
    ocean: ['#0369a1', '#0284c7', '#0ea5e9', '#38bdf8', '#7dd3fc', '#bae6fd'],
    forest: ['#15803d', '#16a34a', '#22c55e', '#4ade80', '#86efac', '#bbf7d0'],
    sunset: ['#ea580c', '#f97316', '#fb923c', '#fdba74', '#fed7aa', '#ffedd5']
  };

  const colors = colorSchemes[colorScheme] || colorSchemes.default;

  // Format data for Recharts
  const chartData = data.data.map((point, index) => ({
    frequency: point.frequency,
    level: point.level || 0,
    octave: point.octave || Math.floor(index / 3),
    fill: colors[index % colors.length]
  }));

  // Calculate peak frequency
  const peakFreq = chartData.reduce((prev, current) => 
    (prev.level > current.level) ? prev : current
  );

  // Calculate average level
  const avgLevel = chartData.reduce((sum, point) => sum + point.level, 0) / chartData.length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Analyse Spectrale - Distribution Fréquentielle</span>
          <div className="flex space-x-2">
            <Badge variant="outline" className="bg-teal-50">
              Moy: {avgLevel.toFixed(1)} dB
            </Badge>
            <Badge variant="outline" className="bg-purple-50">
              Pic: {peakFreq.frequency} Hz ({peakFreq.level.toFixed(1)} dB)
            </Badge>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis 
              dataKey="frequency" 
              label={{ value: 'Fréquence (Hz)', position: 'insideBottom', offset: -10 }}
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis 
              label={{ value: 'Niveau (dB)', angle: -90, position: 'insideLeft' }}
              domain={[0, Math.ceil(Math.max(...chartData.map(d => d.level)) + 10)]}
            />
            <Tooltip 
              contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '8px', border: '1px solid #e2e8f0' }}
              formatter={(value) => [`${value.toFixed(2)} dB`, 'Niveau']}
              labelFormatter={(label) => `Fréquence: ${label} Hz`}
            />
            <Legend wrapperStyle={{ paddingTop: '20px' }} />
            <Bar dataKey="level" name="Niveau sonore">
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>

        {/* Octave Bands Analysis */}
        <div className="mt-6 p-4 bg-slate-50 rounded-lg">
          <h4 className="font-medium mb-3">Analyse par Bandes d'Octave</h4>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3 text-sm">
            {chartData
              .filter((_, index) => index % 2 === 0) // Show every other frequency for octave approximation
              .slice(0, 6)
              .map((point, index) => (
                <div key={index} className="text-center p-2 bg-white rounded border">
                  <div className="text-slate-500 text-xs">{point.frequency} Hz</div>
                  <div className="font-semibold text-lg mt-1">{point.level.toFixed(1)}</div>
                  <div className="text-xs text-slate-400">dB</div>
                </div>
              ))}
          </div>
        </div>

        {/* Frequency Bands Classification */}
        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-medium mb-3">Classification des Bandes de Fréquence</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="bg-white p-3 rounded">
              <div className="font-medium text-blue-700">Basses Fréquences (20-250 Hz)</div>
              <div className="text-2xl font-bold mt-2">
                {chartData
                  .filter(d => d.frequency >= 20 && d.frequency <= 250)
                  .reduce((sum, d) => sum + d.level, 0)
                  .toFixed(1)} dB
              </div>
              <div className="text-xs text-slate-500 mt-1">Rumbles, basses</div>
            </div>
            <div className="bg-white p-3 rounded">
              <div className="font-medium text-green-700">Moyennes Fréquences (250-2000 Hz)</div>
              <div className="text-2xl font-bold mt-2">
                {chartData
                  .filter(d => d.frequency > 250 && d.frequency <= 2000)
                  .reduce((sum, d) => sum + d.level, 0)
                  .toFixed(1)} dB
              </div>
              <div className="text-xs text-slate-500 mt-1">Voix, instruments</div>
            </div>
            <div className="bg-white p-3 rounded">
              <div className="font-medium text-purple-700">Hautes Fréquences (2000-20000 Hz)</div>
              <div className="text-2xl font-bold mt-2">
                {chartData
                  .filter(d => d.frequency > 2000)
                  .reduce((sum, d) => sum + d.level, 0)
                  .toFixed(1)} dB
              </div>
              <div className="text-xs text-slate-500 mt-1">Sifflements, aigus</div>
            </div>
          </div>
        </div>

        {/* Statistics Summary */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-slate-500">Sonomètre:</span>
            <div className="font-semibold">{soundmeterId}</div>
          </div>
          <div>
            <span className="text-slate-500">Bandes analysées:</span>
            <div className="font-semibold">{data.data.length}</div>
          </div>
          <div>
            <span className="text-slate-500">Plage:</span>
            <div className="font-semibold">
              {Math.min(...chartData.map(d => d.frequency))}-{Math.max(...chartData.map(d => d.frequency))} Hz
            </div>
          </div>
          <div>
            <span className="text-slate-500">Résolution:</span>
            <div className="font-semibold">1/3 Octave</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SpectrumGraph;
